import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import CloseIcon from "@material-ui/icons/Close";
import CopyIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatPhone } from "../../utils/formatPhone";

// Marca do Claude (sunburst da Anthropic), cor coral. Desenhada como raios
// arredondados de comprimento alternado, irradiando do centro.
const ClaudeMark = ({ size = 22, color = "#D97757" }) => {
  const rays = Array.from({ length: 12 }).map((_, i) => {
    const a = ((i * 30 - 90) * Math.PI) / 180;
    const r2 = i % 2 === 0 ? 10.4 : 8.2; // comprimento alternado → burst orgânico
    const x1 = 12 + 2.6 * Math.cos(a);
    const y1 = 12 + 2.6 * Math.sin(a);
    const x2 = 12 + r2 * Math.cos(a);
    const y2 = 12 + r2 * Math.sin(a);
    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
  });
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      <g stroke={color} strokeWidth="2.1" strokeLinecap="round">
        {rays}
      </g>
    </svg>
  );
};

// switch: off #CBD1D8 / on verde (var(--ok))
const NumeraSwitch = withStyles(theme => ({
  root: { width: 40, height: 22, padding: 0, display: "flex" },
  switchBase: {
    padding: 3, color: "#fff",
    "&$checked": { transform: "translateX(18px)", color: "#fff", "& + $track": { opacity: 1, background: theme.app.ok || "#15784B" } },
  },
  thumb: { width: 16, height: 16, boxShadow: "none" },
  track: { borderRadius: 22, opacity: 1, background: "#CBD1D8" },
  checked: {},
}))(Switch);

const useStyles = makeStyles(theme => ({
  paper: { borderRadius: 16 },
  head: { display: "flex", alignItems: "flex-start", gap: 12, padding: "20px 20px 0" },
  iconChip: {
    width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: "#F4ECE3",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  titleCol: { flex: 1, minWidth: 0 },
  name: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 16, color: theme.app.ink },
  subtitle: { fontSize: 12.5, color: theme.app.textTertiary, marginTop: 2 },
  pill: {
    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, borderRadius: 999,
    padding: "3px 10px", marginTop: 8, whiteSpace: "nowrap",
  },
  pillOn: { background: "#EAF6EE", color: "#15784B" },
  pillOff: { background: "#FBF3E4", color: "#B7791F" },
  dot: { width: 6, height: 6, borderRadius: "50%", background: "currentColor" },
  closeBtn: { color: theme.app.textTertiary, padding: 5, marginTop: -4, marginRight: -4, "& svg": { fontSize: 19 } },

  intro: { fontSize: 13.5, color: theme.app.textSecondary, lineHeight: 1.55, margin: "16px 0 4px" },
  introEx: { fontStyle: "italic", color: theme.app.textTertiary },

  section: { marginTop: 20 },
  label: { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: theme.app.textTertiary, marginBottom: 8 },
  selectInput: { fontFamily: theme.app.mono, fontSize: 14 },
  hint: { fontSize: 12, color: theme.app.textTertiary, marginTop: 6 },

  toggleRow: { display: "flex", alignItems: "center", gap: 12, marginTop: 18, padding: "12px 0", borderTop: `1px solid ${theme.app.lineSoft}` },
  toggleText: { flex: 1, minWidth: 0 },
  toggleTitle: { fontSize: 14, fontWeight: 600, color: theme.app.ink },
  toggleDesc: { fontSize: 12.5, color: theme.app.textTertiary, marginTop: 2, lineHeight: 1.5 },

  urlBox: { display: "flex", alignItems: "center", gap: 6, background: theme.app.dark || "#1B222A", borderRadius: 10, padding: "10px 8px 10px 12px" },
  url: { flex: 1, fontFamily: theme.app.mono, fontSize: 13, color: "#E6EAF0", wordBreak: "break-all" },
  copyBtn: { color: "#9AA3AD", padding: 5, "& svg": { fontSize: 16 }, "&:hover": { color: "#fff" } },
  steps: { listStyle: "none", padding: 0, margin: "12px 0 0", display: "flex", flexDirection: "column", gap: 10 },
  step: { display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: theme.app.textSecondary, lineHeight: 1.5 },
  stepNum: {
    width: 20, height: 20, borderRadius: 999, flexShrink: 0, background: theme.app.chip || "#EEF0F3", color: theme.app.ink2 || "#5B6470",
    fontSize: 11.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1,
  },
  footer: { padding: "12px 20px 16px" },
  btn: { textTransform: "none", fontWeight: 600, borderRadius: 8 },
  btnPrimary: { background: theme.app.dark || "#2C353D", color: "#fff", boxShadow: "none", "&:hover": { background: "#1f262c", boxShadow: "none" } },
}));

// Modal do conector "Claude (MCP)". Princípio: estado é controle, não alerta.
// Toggle aplica na hora; número de origem é editável (select dos DIDs).
const McpConnectorModal = ({ open, connector, onClose, onSaved }) => {
  const classes = useStyles();
  const [numbers, setNumbers] = useState([]);
  const [busy, setBusy] = useState(false);
  const mcpUrl = `${window.location.origin}/mcp`;
  const active = !!connector;
  const defaultNum = numbers.find(n => n.isDefault) || null;

  const loadNumbers = useCallback(async () => {
    try {
      const { data } = await api.get("/numbers");
      // sem o número de teste da Numera: não serve de origem do MCP (STU-159)
      setNumbers(((data && data.numbers) || []).filter(n => !n.isTestNumber));
    } catch {
      setNumbers([]);
    }
  }, []);

  useEffect(() => {
    if (open) loadNumbers();
  }, [open, loadNumbers]);

  // troca o número padrão (origem das ligações)
  const setDefaultNumber = async id => {
    setBusy(true);
    try {
      await api.put(`/numbers/${id}`, { isDefault: true });
      await loadNumbers();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  // liga/desliga o conector (aplica na hora)
  const toggleActive = async () => {
    setBusy(true);
    try {
      if (active) {
        await api.delete(`/connectors/${connector.id}`);
      } else {
        await api.post("/connectors", { type: "mcp", name: "Claude (MCP)", config: {} });
      }
      onSaved && (await onSaved());
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(mcpUrl);
    toast.success("URL copiada!");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: classes.paper }}>
      <div className={classes.head}>
        <div className={classes.iconChip}><ClaudeMark /></div>
        <div className={classes.titleCol}>
          <div className={classes.name}>Claude (MCP) · Ligações e SMS</div>
          <div className={classes.subtitle}>O Claude faz ligações e envia SMS pela sua conta Numera.</div>
          <span className={`${classes.pill} ${active ? classes.pillOn : classes.pillOff}`}>
            <span className={classes.dot} />
            {active ? "Ativo" : "Inativo"}
          </span>
        </div>
        <IconButton className={classes.closeBtn} onClick={onClose}><CloseIcon /></IconButton>
      </div>

      <DialogContent>
        <div className={classes.intro}>
          Peça em linguagem natural — <span className={classes.introEx}>“ligue para (11) 98765-4321 e confirme a consulta”</span> ou <span className={classes.introEx}>“manda um SMS lembrando do horário”</span>.
        </div>

        <div className={classes.section}>
          <div className={classes.label}>Número de origem (ligações)</div>
          {numbers.length ? (
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              value={defaultNum ? defaultNum.id : ""}
              onChange={e => setDefaultNumber(e.target.value)}
              disabled={busy}
              InputProps={{ className: classes.selectInput }}
            >
              {numbers.map(n => (
                <MenuItem key={n.id} value={n.id} style={{ fontFamily: "monospace" }}>
                  {formatPhone(n.number)}{n.label ? ` · ${n.label}` : ""}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <div className={classes.hint}>Você ainda não tem números na conta. Adicione um em Números.</div>
          )}
          <div className={classes.hint}>As ligações saem deste número (o SMS não precisa dele). Pode trocar quando quiser.</div>
        </div>

        <div className={classes.toggleRow}>
          <div className={classes.toggleText}>
            <div className={classes.toggleTitle}>Conector ativo</div>
            <div className={classes.toggleDesc}>Enquanto desligado, o Claude não consegue ligar nem enviar SMS pela sua conta.</div>
          </div>
          <NumeraSwitch checked={active} onChange={toggleActive} disabled={busy} />
        </div>

        <div className={classes.section}>
          <div className={classes.label}>Conectar no Claude</div>
          <div className={classes.urlBox}>
            <span className={classes.url}>{mcpUrl}</span>
            <IconButton size="small" className={classes.copyBtn} onClick={copyUrl} title="Copiar"><CopyIcon /></IconButton>
          </div>
          <ol className={classes.steps}>
            <li className={classes.step}><span className={classes.stepNum}>1</span><span>No claude.ai, abra <b>Configurações → Conectores</b> e clique em <b>Adicionar conector personalizado</b>.</span></li>
            <li className={classes.step}><span className={classes.stepNum}>2</span><span>Cole a <b>URL</b> acima e confirme.</span></li>
            <li className={classes.step}><span className={classes.stepNum}>3</span><span>Na tela de autorização, clique <b>Autorizar</b> (logado na Numera neste navegador).</span></li>
          </ol>
        </div>
      </DialogContent>

      <DialogActions className={classes.footer}>
        <Button className={classes.btn} onClick={onClose}>Fechar</Button>
        <Button className={`${classes.btn} ${classes.btnPrimary}`} variant="contained" onClick={onClose} startIcon={<CheckIcon />}>
          Concluir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default McpConnectorModal;
